import React, { useState, useEffect } from 'react';
import { X, BedDouble, ChevronRight, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const careLabels = {
  nursing_care: 'Nursing',
  residential_care: 'Residential',
  dementia_nursing: 'Dementia Nursing',
  dementia_residential: 'Dementia Residential',
  mental_health_nursing: 'Mental Health',
  bariatric_nursing: 'Bariatric',
};

const BedAvailabilityPopup = () => {
  const [visible, setVisible] = useState(false);
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    axios.get(`${API}/availability`)
      .then((res) => setAvailability(res.data))
      .catch((err) => console.error('Error fetching availability:', err));
    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const close = () => setVisible(false);

  const enquire = () => {
    setVisible(false);
    setTimeout(() => {
      const el = document.querySelector('#contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 150);
  };

  if (!visible || !availability) return null;

  const availableTypes = Object.keys(careLabels).filter(
    (k) => availability[k]?.available
  );
  const totalBeds = availableTypes.reduce(
    (sum, k) => sum + (availability[k]?.beds || 0),
    0
  );

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:bottom-20 sm:right-6 z-[60] w-auto sm:w-[320px] max-w-md animate-in slide-in-from-bottom-4 fade-in duration-500"
      data-testid="bed-availability-popup"
      role="dialog"
      aria-labelledby="bed-popup-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200/80 overflow-hidden ring-1 ring-black/5">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#1e3a5f] to-[#2a4e7a] px-4 py-3 text-white flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#c9a227]/25 border border-[#c9a227]/40 flex items-center justify-center shrink-0">
            <BedDouble className="w-4 h-4 text-[#f0c64a]" />
          </div>
          <div className="flex-1 min-w-0 pr-6">
            <div className="text-[10px] uppercase tracking-wider text-[#f0c64a] font-semibold">
              Live availability
            </div>
            <div id="bed-popup-title" className="text-sm font-bold leading-tight">
              {totalBeds > 0
                ? `${totalBeds} bed${totalBeds === 1 ? '' : 's'} available today`
                : 'Join our waiting list'}
            </div>
          </div>
          <button
            onClick={close}
            className="absolute top-2 right-2 w-7 h-7 rounded-full hover:bg-white/15 flex items-center justify-center text-white/80 hover:text-white transition-colors"
            aria-label="Close"
            data-testid="bed-popup-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          {availableTypes.length > 0 ? (
            <ul className="space-y-1.5 mb-3">
              {availableTypes.slice(0, 4).map((k) => (
                <li
                  key={k}
                  className="flex items-center justify-between text-xs"
                  data-testid={`popup-row-${k}`}
                >
                  <span className="flex items-center gap-1.5 text-slate-700">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    {careLabels[k]}
                  </span>
                  <span className="font-semibold text-[#1e3a5f]">
                    {availability[k].beds} bed{availability[k].beds === 1 ? '' : 's'}
                  </span>
                </li>
              ))}
              {availableTypes.length > 4 && (
                <li className="text-[11px] text-slate-500 pl-5">
                  + {availableTypes.length - 4} more care services
                </li>
              )}
            </ul>
          ) : (
            <p className="text-xs text-slate-600 mb-3">
              No immediate vacancies. Leave your details and we'll be in touch as soon as a room becomes available.
            </p>
          )}

          <button
            onClick={enquire}
            className="w-full inline-flex items-center justify-center gap-1.5 bg-[#c9a227] hover:bg-[#d4b03a] text-[#1e3a5f] rounded-full py-2 text-xs font-bold transition-colors"
            data-testid="bed-popup-enquire-btn"
          >
            Enquire Now
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BedAvailabilityPopup;
